import { TaskTypeEnum } from '@sr-sdks/api-sdk-axios';
import {
  TASKS_DEFAULT_PARAMS,
  type TasksPaginatedParams,
  fetchTaskFilterOptions,
  fetchTasksPaginated,
} from '../../../api/task/taskApi';
import {
  getTaskCardTitle,
  getTaskEstimatedValue,
} from '../../../components/TaskCard';
import { formatTitleType } from '../../../components/TitleCard/titleMeta';
import type { WorkspaceTask } from '../../../types/taskTypes';
import { createClientNameLookup } from '../../../utils/csv/clientNameLookup';
import {
  type CsvContextResolver,
  csvRequestConfig,
} from '../../../utils/csv/csvExport';
import {
  type TitleDetails,
  createTitleDetailsLookup,
} from '../../../utils/csv/titleDetailsLookup';
import {
  type CsvColumn,
  csvColumn,
  csvDate,
} from '../../../utils/csv/toCsvChunk';
import {
  getOtherClaimantName,
  getTaskClientId,
} from '../../../utils/helpers/clients';
import { ensureStableSort } from '../../../utils/helpers/sort';
import {
  optionClientNames,
  optionOtherClaimantNames,
} from './taskFilterOptions';

export type TasksCsvContext = {
  clientNames: Map<string, string>;
  otherClaimantNames: Map<string, string>;
  /** Competing claims only: year and program type by title id. */
  titles: Map<string, TitleDetails>;
};

type TaskPartyNames = Omit<TasksCsvContext, 'titles'>;

type TasksCsvColumn = CsvColumn<WorkspaceTask, TasksCsvContext>;

/** Only the three tabs export; other task types have no Tasks-page list. */
export const TASK_EXPORT_PAGE: Partial<Record<TaskTypeEnum, string>> = {
  [TaskTypeEnum.CompetingClaim]: 'competing-claims',
  [TaskTypeEnum.DraftRegistration]: 'drafts',
  [TaskTypeEnum.PotentialClaim]: 'potential-claims',
};

const col = csvColumn<WorkspaceTask, TasksCsvContext>;

const rowTitle = (row: WorkspaceTask, ctx: TasksCsvContext) =>
  row.titleId ? ctx.titles.get(row.titleId) : undefined;

const clientName = (row: WorkspaceTask, ctx: TasksCsvContext) => {
  const clientId = getTaskClientId(row);

  return clientId ? ctx.clientNames.get(clientId) : undefined;
};

/**
 * Each tab's card fields, in card order (APP-2510 D17). The other claimant is
 * exported by name only: its workspace and client ids are another member's.
 */
export const tasksCsvColumns = (type: TaskTypeEnum): TasksCsvColumn[] => {
  const title = col('title', 'Title', (row) => getTaskCardTitle(row, type));
  const year = col('year', 'Year', (row) => row.details?.yearOfProduction);
  const programType = col(
    'programType',
    'Program type',
    (row) => row.details?.workType,
  );
  const client = col('client', 'Client', clientName, true);
  const estimatedValue = col(
    'estimatedValue',
    // The card shows AUD; the file has no Currency column to say so.
    'Est. value (AUD)',
    (row) => {
      const value = getTaskEstimatedValue(row);

      // A sum of floats, so round to cents as the card does: 1234.56 + 789.12
      // would otherwise export as 2023.6799999999998.
      return value === undefined ? undefined : Math.round(value * 100) / 100;
    },
  );
  const dueDate = col('dueDate', 'Due date', (row) => csvDate(row.dueDate));

  if (type === TaskTypeEnum.DraftRegistration) {
    return [
      title,
      year,
      programType,
      client,
      col('created', 'Date created', (row) => csvDate(row.created)),
    ];
  }

  if (type === TaskTypeEnum.CompetingClaim) {
    return [
      title,
      // The task's own copies can lag the title's, or be missing (competing
      // claims carry no workType); the title is the record.
      col(
        'year',
        'Year',
        (row, ctx) => rowTitle(row, ctx)?.year || row.details?.yearOfProduction,
      ),
      col(
        'programType',
        'Program type',
        (row, ctx) =>
          formatTitleType(rowTitle(row, ctx)?.titleType) ??
          row.details?.workType,
      ),
      // The competing card has no Client field: its Claimant is the task's client.
      col('claimant', 'Claimant', clientName, true),
      col('otherClaimant', 'Other claimant', (row, ctx) =>
        getOtherClaimantName(row, ctx.otherClaimantNames),
      ),
      col('status', 'Status', (row) => row.taskDisplayStatus),
      estimatedValue,
      dueDate,
    ];
  }

  return [title, year, programType, client, estimatedValue, dueDate];
};

export const fetchTasksCsvPage = (
  params: TasksPaginatedParams,
  page: number,
  pageSize: number,
  signal: AbortSignal,
) =>
  fetchTasksPaginated(
    {
      ...params,
      page,
      pageSize,
      // The tasks API's own default, not the invoice one ensureStableSort
      // falls back to, so an unsorted tab exports in the order it lists.
      sortBy: ensureStableSort(params.sortBy, TASKS_DEFAULT_PARAMS.sortBy),
    },
    csvRequestConfig(signal),
  );

/**
 * Names for the task cards' parties from the same sources `useTaskPartyNames`
 * uses, in the reverse order: the tab's filter options first (fetched once per
 * export, uncached), then a client lookup only for clients they couldn't name.
 * The card asks the lookup first; this saves a lookup per page, at the cost of
 * a rare rename mismatch (APP-2510 design, risks). Competing claims also read
 * each row's title for its year, alongside the names. Make one per export.
 */
export const tasksCsvContext = (
  workspaceId: string,
  type: TaskTypeEnum,
  isAgent: boolean,
  // Set when the export lists resolved competing claims, whose other
  // claimants the open-only options can't name (APP-2577).
  includeResolved?: boolean,
): CsvContextResolver<WorkspaceTask, TasksCsvContext> => {
  const lookupClients = createClientNameLookup(workspaceId);
  const lookupTitles = createTitleDetailsLookup(workspaceId);
  let options: Promise<TaskPartyNames> | undefined;

  const loadOptions = (signal: AbortSignal) => {
    options ??= fetchTaskFilterOptions(
      { includeResolved, type, workspaceId },
      csvRequestConfig(signal),
    )
      .then(({ clients, otherClaimants }) => ({
        clientNames: optionClientNames(clients),
        // Unknown claimants export blank: the UI placeholder isn't data.
        otherClaimantNames: optionOtherClaimantNames(otherClaimants, ''),
      }))
      .catch((error: unknown) => {
        // Let a retry ask again rather than replay the failure.
        options = undefined;
        throw error;
      });

    return options;
  };

  const resolveNames = async (
    rows: WorkspaceTask[],
    signal: AbortSignal,
  ): Promise<TaskPartyNames> => {
    // Outside an agent workspace only Competing Claims reads a name (the other
    // claimant), so other tabs skip the options request altogether.
    if (!isAgent && type !== TaskTypeEnum.CompetingClaim) {
      return { clientNames: new Map(), otherClaimantNames: new Map() };
    }

    const { clientNames, otherClaimantNames } = await loadOptions(signal);

    if (!isAgent) return { clientNames: new Map(), otherClaimantNames };

    const unlabelled = rows
      .map(getTaskClientId)
      .filter((id): id is string => typeof id === 'string' && id !== '')
      .filter((id) => !clientNames.has(id));
    const looked = unlabelled.length
      ? await lookupClients(unlabelled, signal)
      : new Map<string, string>();

    return {
      clientNames: new Map([...clientNames, ...looked]),
      otherClaimantNames,
    };
  };

  const resolveTitles = (rows: WorkspaceTask[], signal: AbortSignal) => {
    const titleIds = rows
      .map((row) => row.titleId)
      .filter((id): id is string => typeof id === 'string' && id !== '');

    return type === TaskTypeEnum.CompetingClaim && titleIds.length
      ? lookupTitles(titleIds, signal)
      : Promise.resolve(new Map<string, TitleDetails>());
  };

  return async (rows, signal) => {
    const [names, titles] = await Promise.all([
      resolveNames(rows, signal),
      resolveTitles(rows, signal),
    ]);

    return { ...names, titles };
  };
};
